/**
 * @file OrderCreateItemsCard.tsx
 * @description Карточка «Позиции» формы создания заказа (вынесено из
 * `OrderCreatePage` в phase19.24 шаг 5.1).
 *
 * Две дороги к составу, как и раньше: пустая строка с inline-подбором SKU и
 * боковой каталог с множественным выбором. Шаг и проверка количества — по
 * единице счёта строки (`qtyInputProps`), чтобы в заказ не попала «половина
 * пачки».
 *
 * Итог здесь не печатаем: сумма к оплате живёт в карточке «Итого и отправка»
 * вместе с доставкой — двух разных «Итого» на одном экране быть не должно.
 */

import { List, Plus, Trash2 } from "lucide-react";

import { OrderItemRowInput } from "./OrderItemRowInput";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatMoney, type OrderSkuDto } from "../../lib/api-orders";
import type { OrderCreateRow } from "../../lib/order-create-rows";
import { qtyInputProps } from "../../lib/qty-input";

interface Props {
  rows: OrderCreateRow[];
  onAddEmpty: () => void;
  onOpenCatalog: () => void;
  onPickSku: (index: number, sku: OrderSkuDto) => void;
  onPatch: (index: number, patch: Partial<OrderCreateRow>) => void;
  onRemove: (index: number) => void;
}

export function OrderCreateItemsCard({
  rows,
  onAddEmpty,
  onOpenCatalog,
  onPickSku,
  onPatch,
  onRemove,
}: Props) {
  // До 640 — одна строка из двух равных колонок, подпись переносится внутри
  // кнопки: так кнопки не выходят за карточку (QA-5 D5). Тач-цель ≥48px.
  const actions = (
    <div className="flex flex-wrap gap-2 max-sm:grid max-sm:grid-cols-2">
      <Button variant="outline" className="min-h-12 max-sm:min-w-0 max-sm:whitespace-normal" onClick={onAddEmpty}>
        <Plus className="mr-1 size-4" />
        Позиция
      </Button>
      <Button className="min-h-12 max-sm:min-w-0 max-sm:whitespace-normal" onClick={onOpenCatalog}>
        <List className="mr-1 size-4" />
        Из каталога
      </Button>
    </div>
  );

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Позиции"
        subtitle="Введите название или артикул прямо в строке либо выберите несколько из каталога."
        // На 360px колонка `auto` съедала шапку: подзаголовок переносился по
        // одному слову, кнопки вставали друг под друга. Под заголовком обоим
        // хватает места (QA 2026-09-21).
        actionPlacement="below-on-mobile"
        action={actions}
      />
      <EditorialCard.Body>
        {rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center text-on-surface-variant">
            <p className="text-sm">Добавьте первую позицию.</p>
            {actions}
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((row, index) => (
              <li
                key={`${row.skuId || "empty"}-${index}`}
                className="flex flex-wrap items-center gap-3 rounded-xl bg-surface-low p-3"
              >
                {row.skuId ? (
                  <>
                    <div className="min-w-40 flex-1">
                      <div className="text-sm font-medium">{row.productName}</div>
                      <Badge variant="outline" className="mt-0.5 font-mono text-xs">
                        {row.skuCode}
                      </Badge>
                    </div>
                    <div className="flex w-24 flex-col gap-1">
                      <Label className="text-xs" htmlFor={`row-qty-${index}`}>
                        Кол-во, {row.countUnit}
                      </Label>
                      <Input
                        id={`row-qty-${index}`}
                        className="min-h-12"
                        type="number"
                        min="0"
                        step={qtyInputProps(row.countUnit).step}
                        inputMode={qtyInputProps(row.countUnit).inputMode}
                        value={row.quantity}
                        onChange={(e) => onPatch(index, { quantity: e.target.value })}
                      />
                    </div>
                    <div className="flex w-32 flex-col gap-1">
                      <Label className="text-xs" htmlFor={`row-price-${index}`}>
                        Цена, ₽
                      </Label>
                      <Input
                        id={`row-price-${index}`}
                        className="min-h-12"
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.priceGross}
                        onChange={(e) => onPatch(index, { priceGross: e.target.value })}
                      />
                    </div>
                    <div className="w-28 text-right">
                      <div className="text-xs text-on-surface-variant">Сумма</div>
                      <div className="font-semibold tabular-nums text-primary">
                        {formatMoney(
                          String(
                            (Number(row.priceGross) || 0) * (Number(row.quantity) || 0),
                          ),
                        )}{" "}
                        ₽
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="min-w-60 flex-1">
                    <OrderItemRowInput onSelect={(sku) => onPickSku(index, sku)} />
                  </div>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="size-12"
                  onClick={() => onRemove(index)}
                  aria-label="Удалить позицию"
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
